import Modal from "@via/components/modal";
import SelectComponent from "@via/components/select/select";
import { ModalSize } from "@via/utils/constants";
import {
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth } from "@via/hooks/useAuth.tsx";
import { useVmetaProjectManagement } from "@via/pages/data/hooks/useVmetaProjectManagement";
import { useCreateVmetaField } from "@via/pages/projects/metadata/metadata.api.tsx";
import {
  useGetFileCollectionId,
  useGetProjectDatasetFields,
} from "@via/pages/data/premium-dataset/premium-dataset.api.tsx";
import { useNotifications } from "@via/hooks/useNotifications";
import { NOTIFICATION } from "@via/context/notifications-context";
import { SelectOption } from "@via/utils/interfaces";
import AdditionalFieldForm from "./additional-field-form";
import { FieldFormData } from "@via/pages/data/premium-dataset/utils/helper";
import {
  InformationCircleIcon,
  MinusIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import {
  BannerType,
  MessageBanner,
} from "@via/components/message-banner/message-banner";

interface AddPremiumDatasetFieldsModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * If provided, modal will auto-select this Sample Tracker on open.
   * If not provided, modal will auto-select (or create) the "default" Sample Tracker.
   */
  initialCanvasId?: string;
  onFieldsCreated?: () => void | Promise<void>;
}

const AddPremiumDatasetFieldsModal: FC<AddPremiumDatasetFieldsModalProps> = ({
  isOpen,
  onClose,
  initialCanvasId,
  onFieldsCreated,
}) => {
  const { user } = useAuth();
  const { setNotification } = useNotifications();

  const {
    vmetaProjectOptions,
    selectedVmetaProject,
    setSelectedVmetaProject,
    isLoading: isVmetaProjectsLoading,
    setSearch: debouncedHandleInputChange,
    fetchMoreOptions: fetchMoreVmetaProjects,
    createProject: handleCreateOption,
  } = useVmetaProjectManagement({
    userId: user?.vmeta?.userId,
    enabled: isOpen,
    initialProjectId: initialCanvasId,
    // Preserve existing behavior: this modal lists trackers without viewType/user filtering.
    enableViewTypeFilter: false,
  });

  const handleVmetaProjectChange = useCallback(
    (selected: SelectOption | readonly SelectOption[] | null) => {
      const next = Array.isArray(selected) ? (selected[0] ?? null) : selected;
      setSelectedVmetaProject(next);
    },
    [setSelectedVmetaProject]
  );

  // ---- Field creation UI (copied/adapted from ManualAddFieldsModal) ----
  const [fieldsCount, setFieldsCount] = useState(1);
  const [fieldDataMap, setFieldDataMap] = useState<
    Record<string, FieldFormData>
  >({});
  const [isCreating, setIsCreating] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const fieldDataMapRef = useRef<Record<string, FieldFormData>>({});

  const generateFieldNames = useCallback((count: number): string[] => {
    return Array.from({ length: count }, (_, index) => `Field ${index + 1}`);
  }, []);

  const fieldNames = useMemo(
    () => generateFieldNames(fieldsCount),
    [fieldsCount, generateFieldNames]
  );

  const [selectedFields, setSelectedFields] = useState<Set<string>>(
    new Set(fieldNames)
  );

  useEffect(() => {
    setSelectedFields(new Set(fieldNames));
  }, [fieldNames]);

  const handleFieldDataChange = useCallback(
    (fieldName: string, data: FieldFormData) => {
      fieldDataMapRef.current = {
        ...fieldDataMapRef.current,
        [fieldName]: data,
      };
      setFieldDataMap(fieldDataMapRef.current);
    },
    []
  );

  const handleFieldSelection = useCallback(
    (fieldName: string, isSelected: boolean) => {
      setSelectedFields(prev => {
        const newSet = new Set(prev);
        if (isSelected) newSet.add(fieldName);
        else newSet.delete(fieldName);
        return newSet;
      });
    },
    []
  );

  const handleFieldsCountChange = useCallback(
    (newCount: number) => {
      if (newCount < 1) return;
      if (newCount > 10) return;

      setFieldsCount(newCount);

      const newFieldNames = generateFieldNames(newCount);
      const newFieldDataMap = { ...fieldDataMapRef.current };
      const newSelectedFields = new Set<string>();

      Object.keys(newFieldDataMap).forEach(fieldName => {
        if (!newFieldNames.includes(fieldName)) {
          delete newFieldDataMap[fieldName];
        }
      });

      selectedFields.forEach(fieldName => {
        if (newFieldNames.includes(fieldName)) {
          newSelectedFields.add(fieldName);
        }
      });

      fieldDataMapRef.current = newFieldDataMap;
      setFieldDataMap(newFieldDataMap);
      setSelectedFields(newSelectedFields);
    },
    [generateFieldNames, selectedFields]
  );

  const { mutateAsync: getFileCollectionId } = useGetFileCollectionId();
  const { mutateAsync: createVmetaField } = useCreateVmetaField();
  const { mutateAsync: getProjectDatasetFields } = useGetProjectDatasetFields();

  const [collectionId, setCollectionId] = useState<string>("");
  const [isCollectionIdLoading, setIsCollectionIdLoading] = useState(false);

  useEffect(() => {
    let isCancelled = false;
    const run = async () => {
      if (!isOpen) return;
      if (!selectedVmetaProject?.value) return;
      setIsCollectionIdLoading(true);
      try {
        const id = await getFileCollectionId(
          selectedVmetaProject.value as string
        );
        if (!isCancelled) setCollectionId(id);
      } catch (_e) {
        if (!isCancelled) {
          setCollectionId("");
          setNotification({
            id: "collection-id-error",
            message: "Failed to Get Collection Information",
            details:
              "Could not retrieve collection information for field creation.",
            variant: NOTIFICATION.ERROR,
          });
        }
      } finally {
        if (!isCancelled) setIsCollectionIdLoading(false);
      }
    };
    run();
    return () => {
      isCancelled = true;
    };
  }, [
    isOpen,
    selectedVmetaProject?.value,
    getFileCollectionId,
    setNotification,
  ]);

  const selectedFieldsValid = useMemo(() => {
    return Array.from(selectedFields).every(fieldName => {
      const fieldData = fieldDataMap[fieldName];
      return fieldData && fieldData.name && fieldData.label && fieldData.type;
    });
  }, [selectedFields, fieldDataMap]);

  const hasSelectedFields = selectedFields.size > 0;

  const handleClose = useCallback(() => {
    setFieldsCount(1);
    setFieldDataMap({});
    setSelectedFields(new Set());
    setIsCreating(false);
    setErrors([]);
    fieldDataMapRef.current = {};
    debouncedHandleInputChange("");
    setSelectedVmetaProject(null);
    setCollectionId("");
    onClose();
  }, [debouncedHandleInputChange, onClose, setSelectedVmetaProject]);

  const parseVmetaValidationErrors = useCallback((err: any): string[] => {
    const apiErrors = err?.response?.data?.errors;
    if (Array.isArray(apiErrors) && apiErrors.length > 0) {
      return apiErrors.map((e: any) => {
        const field = e?.field ?? "unknown field";
        const message = e?.message ?? "unknown error";
        return `${field}: ${message}`;
      });
    }
    const apiMessage = err?.response?.data?.message;
    if (typeof apiMessage === "string" && apiMessage.length > 0) {
      return [apiMessage];
    }
    if (typeof err?.message === "string" && err.message.length > 0) {
      return [err.message];
    }
    return ["Unknown error"];
  }, []);

  const handleCreateFields = useCallback(async () => {
    if (!selectedVmetaProject?.value) return;
    setIsCreating(true);
    setErrors([]);
    try {
      const fieldsToCreate = Array.from(selectedFields)
        .map(fieldName => fieldDataMap[fieldName])
        .filter(
          fieldData =>
            fieldData && fieldData.name && fieldData.label && fieldData.type
        );

      if (!fieldsToCreate.length) return;

      const finalCollectionId =
        collectionId ||
        (await getFileCollectionId(selectedVmetaProject.value as string));

      const createResults = await Promise.allSettled(
        fieldsToCreate.map(fieldData =>
          createVmetaField({
            name: fieldData.name,
            label: fieldData.label,
            type: fieldData.type as any,
            collectionID: finalCollectionId,
            unique: fieldData.unique,
            required: fieldData.required,
            perms: [],
          })
        )
      );

      const validationMessages: string[] = [];
      createResults.forEach(result => {
        if (result.status === "rejected") {
          validationMessages.push(...parseVmetaValidationErrors(result.reason));
        }
      });

      if (validationMessages.length > 0) {
        setErrors(validationMessages);
        return;
      }

      // Refresh cache for this canvas (useful for subsequent spreadsheet imports)
      await getProjectDatasetFields({
        canvasId: selectedVmetaProject.value as string,
        collectionName: "file",
      });

      await onFieldsCreated?.();
      handleClose();
    } catch (error) {
      console.error("Failed to create fields:", error);
      setErrors(parseVmetaValidationErrors(error));
    } finally {
      setIsCreating(false);
    }
  }, [
    selectedVmetaProject?.value,
    selectedFields,
    fieldDataMap,
    collectionId,
    getFileCollectionId,
    createVmetaField,
    getProjectDatasetFields,
    onFieldsCreated,
    handleClose,
    parseVmetaValidationErrors,
    setNotification,
  ]);

  return (
    <Modal
      open={isOpen}
      setOpen={handleClose}
      title="Add Fields"
      size={ModalSize.Large}
      showButtons={false}
      disableClose={isCreating}>
      <div className="flex h-[75vh] flex-col">
        {errors.length > 0 && (
          <div className="mb-4 max-h-[200px] overflow-auto">
            <MessageBanner messages={errors} type={BannerType.Error} />
          </div>
        )}
        {/* Sample Tracker selection */}
        <div className="flex-shrink-0">
          <label className="mb-2 block text-sm font-medium leading-6 text-gray-900">
            Select Sample Tracker
          </label>
          <SelectComponent
            name="vmetaProjectId"
            options={vmetaProjectOptions}
            value={selectedVmetaProject}
            onChange={handleVmetaProjectChange}
            isLoading={isVmetaProjectsLoading}
            isCreatable
            showCreateHint={true}
            onCreateOption={handleCreateOption}
            placeholder="Choose sample tracker or type to create"
            enableClear={false}
            onFetchMoreOptions={fetchMoreVmetaProjects}
            onInputChange={inputValue => debouncedHandleInputChange(inputValue)}
          />
          {isCollectionIdLoading && (
            <div className="mt-2 text-xs text-gray-500">
              Loading collection information…
            </div>
          )}
        </div>

        {/* Header Information */}
        <div className="mt-4 flex-shrink-0 rounded-lg bg-green-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <InformationCircleIcon className="h-5 w-5 text-green-500" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">
                Add New Fields
              </h3>
              <div className="mt-2 text-sm text-green-700">
                <p>
                  Create new fields for your Sample Tracker. Configure the field
                  properties and select which fields you want to create.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Field Count Control */}
        <div className="flex-shrink-0 border-b border-gray-200 pb-4 pt-6">
          <div className="flex items-center justify-between">
            <h4 className="text-lg font-medium text-gray-900">
              Number of Fields to Add
            </h4>
            <div className="flex items-center space-x-3">
              <button
                type="button"
                onClick={() => handleFieldsCountChange(fieldsCount - 1)}
                disabled={fieldsCount <= 1}
                className="inline-flex items-center rounded-md bg-white px-2 py-1 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
                <MinusIcon className="h-4 w-4" />
              </button>
              <span className="min-w-[2rem] text-center text-sm font-medium text-gray-900">
                {fieldsCount}
              </span>
              <button
                type="button"
                onClick={() => handleFieldsCountChange(fieldsCount + 1)}
                disabled={fieldsCount >= 10}
                className="inline-flex items-center rounded-md bg-white px-2 py-1 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
                <PlusIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Scrollable Field Forms */}
        <div className="flex-1 overflow-y-auto py-4">
          <div className="space-y-4 pr-2">
            {fieldNames.map(fieldName => (
              <AdditionalFieldForm
                key={fieldName}
                columnName={fieldName}
                onFieldDataChange={data =>
                  handleFieldDataChange(fieldName, data)
                }
                initialData={fieldDataMap[fieldName]}
                isSelected={selectedFields.has(fieldName)}
                onSelectionChange={isSelected =>
                  handleFieldSelection(fieldName, isSelected)
                }
              />
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex-shrink-0 flex justify-end space-x-3 border-t border-gray-200 pt-6">
          <button
            type="button"
            onClick={handleClose}
            disabled={isCreating}
            className="rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50">
            Cancel
          </button>

          <button
            type="button"
            onClick={handleCreateFields}
            disabled={
              !selectedVmetaProject ||
              !hasSelectedFields ||
              !selectedFieldsValid ||
              isCreating ||
              isCollectionIdLoading
            }
            className="inline-flex items-center rounded-md bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600 disabled:opacity-50">
            {`Create ${selectedFields.size} Selected Field${
              selectedFields.size !== 1 ? "s" : ""
            }`}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AddPremiumDatasetFieldsModal;
